<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import type {
  IFixedTrackerPaymentsComposableOptions,
  IFixedTrackerPaymentsForm,
} from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/submodules/fixed-tracker-payments/types';
import type { ITrackerDealChangesModalPageProps } from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/submodules/tracker-deal-changes/pages/TrackerDealChangesModalPage/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VLoader from '@components/loader/VLoader.vue';
import {
  useFixedTrackerPaymentsForm,
} from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/submodules/fixed-tracker-payments/composables';
import {
  TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_FIXED_PAYMENTS,
} from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/submodules/fixed-tracker-payments/constants';
import { useFormatMonthDate } from '@modules/core/composables/datetime';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { assert, isDate, isString } from '@utils/guards';

import {
  TRANSLATION_GROUP_AFFILIATES_TRACKERS_FIXED_PAYMENTS_HISTORY_EDIT,
} from '../../constants';
import { useUpdateFixedPaymentsHistoryItemMutation } from '../../mutations';
import { useFixedPaymentsHistoryItemQuery } from '../../queries';

const props = defineProps<Pick<ITrackerDealChangesModalPageProps, 'backFromEditHistoryRoute' | 'merchantName'>>();

const route = useRoute();
const { formatMonthDate } = useFormatMonthDate();

const merchantId = computed(() => (isString(route.params.merchantId) ? Number(route.params.merchantId) : 0));
const itemId = computed(() => (isString(route.params.itemId) ? Number(route.params.itemId) : 0));

const { data, isLoading } = useFixedPaymentsHistoryItemQuery(itemId, merchantId);

const formOptions = computed<IFixedTrackerPaymentsComposableOptions>(() => ({
  serialIdValue: data.value?.history_of_fixed_payments_change.serial_id,
  paymentSizeValue: data.value?.history_of_fixed_payments_change.payment_size,
  dateValue: isString(data.value?.history_of_fixed_payments_change.date) ? new Date(data.value.history_of_fixed_payments_change.date) : undefined,
  descriptionValue: data.value?.history_of_fixed_payments_change.description,
  merchantNameValue: props.merchantName,
  datePickerSelectionMode: 'single',
}));

const { formSchema, formGroups } = useFixedTrackerPaymentsForm(formOptions);

const {
  mutateAsync,
  isLoading: isUpdateLoading,
  errors,
  clearError,
} = useUpdateFixedPaymentsHistoryItemMutation();

async function onSubmit({ payment_size, date, serial_id, description }: IFixedTrackerPaymentsForm): Promise<void> {
  assert(isDate(date));
  await mutateAsync({
    itemId: itemId.value,
    merchant: merchantId.value,
    history_of_fixed_payments_change: {
      id: itemId.value,
      serial_id,
      payment_size,
      description,
      date: formatMonthDate(date),
    },
  });
}
</script>

<template>
  <VLoader v-data-qa-auto-id="'FixedPaymentsHistoryEditPage'"
    :loading="isLoading"
    min-height="518px"
  >
    <div class="p-5">
      <h4 class="p-card-title mb-3 text-lg">
        {{ $getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_FIXED_PAYMENTS_HISTORY_EDIT, 'HEADER') }}
      </h4>
      <VForm
        :schema="formSchema"
        :groups="formGroups"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_FIXED_PAYMENTS, 'SUBMIT_BUTTON')"
        :is-loading="isUpdateLoading"
        :custom-errors="errors"
        @submit="onSubmit"
        @interacted="clearError"
      >
        <template #secondaryBtn>
          <VButton
            :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_FIXED_PAYMENTS_HISTORY_EDIT, 'CLOSE')"
            :to="backFromEditHistoryRoute"
            outlined
          />
        </template>
      </VForm>
    </div>
  </VLoader>
</template>
